import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Button,
  Center,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDocumentTitle } from "@mantine/hooks";
import { IconAt, IconMailPlus } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { StyledSignIn } from "@pages/SignIn/SignIn.styled";

import { PAGE_TITLE } from "@constants/index";

import { useSendPasswordResetLink } from "@hooks/mutations/user/useSendPasswordResetLink";

import Logo from "@assets/misc/logo.webp";


const schema = () =>
  z.object({
    email: z
      .string()
      .email(i18n.t("email-invalid"))
      .min(1, i18n.t("email-required")),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

export const ForgotPassword = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("forgot-password")} • ${PAGE_TITLE}`);

  const {
    mutate: sendInviteLink,
    isLoading,
    isSuccess,
  } = useSendPasswordResetLink();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(schema()),
    initialValues: {
      email: "",
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    // console.log(values);
    sendInviteLink(
      { email: values.email },
      {
        onSuccess: () => {
          // form.reset();
          return;
        },
      }
    );
  };

  return (
    <StyledSignIn>
      <div className="content">
        <div className="logo">
          <img src={Logo} alt="La Ruche Health" />
        </div>
        {!isSuccess ? (
          <>
            <h1 className="heading">{`${t("forgot-password")}?`}</h1>
            <p className="sub-heading">
              {t("send-password-reset-instructions")}
            </p>

            <form
              className="form"
              onSubmit={form.onSubmit(
                values => handleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  form.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <TextInput
                label={t("email")}
                withAsterisk
                placeholder={t("eg", { example: "johndoe@gmail.com" })}
                leftSection={<IconAt size={16} />}
                {...form.getInputProps("email")}
                key={form.key("email")}
              />

              <Button type="submit" mt={"lg"} loading={isLoading}>
                {t("send-reset-link")}
              </Button>
            </form>
          </>
        ) : (
          <Stack mt="xl" gap="0">
            <Center>
              <Group
                justify="center"
                align="center"
                bg="green.0"
                style={{ borderRadius: "50%", height: 80, width: 80 }}>
                <IconMailPlus
                  size={35}
                  color={"var(--mantine-color-green-6)"}
                  stroke={1.5}
                />
              </Group>
            </Center>
            <Stack gap="xs" mt="md">
              <Title order={1} fw={500} fz={20} mt="md">
                {t("check-email")}
              </Title>
              <Text c="dimmed" fz={15} lh={1.5}>
                {t("sent-instructions-to-email")}
                <Text inherit fw={500}>
                  {form.getValues().email}
                </Text>
              </Text>
            </Stack>
          </Stack>
        )}
        <Link to="/" className="forgot-password hover-underline">
          {t("back-to-login")}
        </Link>
      </div>
    </StyledSignIn>
  );
};
